/**
 * Phase 8 T-17 §23.B — Global toast container.
 *
 * Renders up to 3 toasts in a fixed bottom-right stack. Newest toast is on
 * top (index 0). Older toasts slide down with an 8 px vertical offset each.
 * A 4th toast replaces the oldest one (FIFO eviction).
 *
 * Consumed by `ToastProvider`. Components call `useGlobalToast().showToast`.
 */

import { createPortal } from 'react-dom';
import type { ToastItem } from './Toast';
import { Toast } from './Toast';

interface ToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}

const MAX_TOASTS = 3;
const STACK_OFFSET_PX = 8;

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  // Display newest first — slice ensures max 3 visible.
  const visible = toasts.slice(0, MAX_TOASTS);

  if (visible.length === 0) return null;

  return createPortal(
    <div
      aria-label="Notifications"
      className="pointer-events-none fixed bottom-6 right-6 z-[9999] flex flex-col-reverse gap-0"
      style={{ gap: 0 }}
    >
      {visible.map((item, idx) => {
        const offset = (visible.length - 1 - idx) * STACK_OFFSET_PX;
        return (
          <div
            key={item.id}
            className="pointer-events-auto"
            style={{
              // Stack older toasts slightly behind / below the newest.
              transform: `translateY(${offset}px)`,
              // Subtle scale-down for depth cue on non-frontmost toasts.
              scale: idx === 0 ? '1' : String(1 - (visible.length - 1 - idx) * 0.02),
              zIndex: 9999 - (visible.length - 1 - idx),
              transition: 'transform 0.2s ease, scale 0.2s ease',
              // Keep a fixed margin below so toasts don't collapse.
              marginBottom: idx === 0 ? 0 : -STACK_OFFSET_PX,
            }}
          >
            <Toast item={item} onDismiss={onDismiss} />
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
